import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import { OrderLineItem, OrderLineModifier } from 'models/Order'
import React from 'react'
import {
  currencyFromCents,
  decimalQuantityFormatter,
} from 'utilities/formatters'

const t = buildTranslate('users.view.activity.order_details.receipt')

type Props = {
  item: OrderLineItem
}

// One indent class per nesting depth; a combo modifier's own sub-modifier is
// depth 2. Deeper chains are unexpected, so they collapse onto the last class.
const INDENT_CLASSES = ['pl-m', 'pl-l', 'pl-xl']
const indentClassFor = (depth: number): string =>
  INDENT_CLASSES[Math.min(depth, INDENT_CLASSES.length) - 1]

const ModifierRows = ({
  modifiers,
  parentName,
  depth,
  cellClass,
  numericCellClass,
}: {
  modifiers: OrderLineModifier[]
  parentName: string
  depth: number
  cellClass: string
  numericCellClass: string
}) => (
  <>
    {modifiers.map((modifier, index) => (
      <React.Fragment key={`${index}-${modifier.name}`}>
        <tr>
          <td className={`${indentClassFor(depth)} ${cellClass}`}>
            <span className="sr-only">
              {t('modifier_of', { item: parentName })}
            </span>
            <Text.BodyText4 color="grey70">{modifier.name}</Text.BodyText4>
          </td>
          <td className={numericCellClass}>
            <Text.BodyText4 color="grey70">
              {decimalQuantityFormatter(modifier.quantity)}
            </Text.BodyText4>
          </td>
          <td className={numericCellClass}>
            {modifier.price_cents == null ? (
              <Text.BodyText5 color="grey70">
                {t('modifier_included_above')}
              </Text.BodyText5>
            ) : (
              <Text.BodyText4 color="grey70">
                {currencyFromCents(modifier.price_cents)}
              </Text.BodyText4>
            )}
          </td>
        </tr>
        <ModifierRows
          modifiers={modifier.modifiers ?? []}
          parentName={modifier.name}
          depth={depth + 1}
          cellClass={cellClass}
          numericCellClass={numericCellClass}
        />
      </React.Fragment>
    ))}
  </>
)

const Row = ({ item }: Props) => {
  const [css] = useStyletron()

  const cell = {
    padding: '6px 0',
    verticalAlign: 'top',
    borderBottom: `1px solid ${palette.grey20}`,
  }
  const cellClass = css(cell)
  const numericCellClass = css({ ...cell, textAlign: 'right' })

  return (
    <>
      <tr>
        <td className={cellClass}>
          <Text.BodyText4 tag="div">{item.name}</Text.BodyText4>
          {!!item.notes && (
            <Text.BodyText5 tag="div" color="grey70">
              {item.notes}
            </Text.BodyText5>
          )}
        </td>
        <td className={numericCellClass}>
          <Text.BodyText4>
            {decimalQuantityFormatter(item.quantity)}
          </Text.BodyText4>
        </td>
        <td className={numericCellClass}>
          {item.price_cents == null ? (
            <Text.BodyText5 color="grey70">
              {t('item_price_unavailable')}
            </Text.BodyText5>
          ) : (
            <Text.BodyText4>
              {currencyFromCents(item.price_cents)}
            </Text.BodyText4>
          )}
        </td>
      </tr>
      <ModifierRows
        modifiers={item.modifiers ?? []}
        parentName={item.name}
        depth={1}
        cellClass={cellClass}
        numericCellClass={numericCellClass}
      />
    </>
  )
}

export default Row
